import { Sha256 } from "@vault/shared/sha256";
import { useSyncExternalStore } from "react";
import { client, errorOf, type MultipartUpload, signIn, type UploadResult } from "../api.ts";
import { analysisLabel, queueAnalysis } from "./analysis/analyse.ts";
import { markLanded, versionKey } from "./landed.ts";
import { flashMood } from "./mood.ts";
import { keys, queryClient, refreshAfterUpload, storeNode } from "./queries.ts";

const SINGLE_REQUEST_BYTES = 100 * 1024 * 1024;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;
export const MAX_UPLOAD_LABEL = "5 GB";
const CONCURRENCY = 2;
const PART_ATTEMPTS = 3;
const PART_BACKOFF_MS = 500;
const DONE_LINGER_MS = 6000;

export type UploadTarget =
  | { kind: "new"; parentId: string | null }
  | { kind: "version"; nodeId: string; name: string; parentId: string | null };

export const ROOT_TARGET: UploadTarget = { kind: "new", parentId: null };

type UploadPart = { n: number; etag: string };

/** A multipart session kept across retries so a retry resumes after the last finished part. */
type MultipartSession = { hash: string; started: MultipartUpload; parts: UploadPart[] };

export type Upload = {
  id: string;
  file: File;
  target: UploadTarget;
  phase: "queued" | "preparing" | "uploading" | "done" | "failed";
  loaded: number;
  error: string | null;
  retryable: boolean;
  result: UploadResult | null;
  analysing: boolean;
  multipart: MultipartSession | null;
};

let uploads: readonly Upload[] = [];
const listeners = new Set<() => void>();
const requests = new Map<string, XMLHttpRequest>();
const hashing = new Map<string, AbortController>();

const emit = (next: readonly Upload[]) => {
  uploads = next;
  for (const listener of listeners) listener();
};

const update = (id: string, change: Partial<Upload>) =>
  emit(uploads.map((upload) => (upload.id === id ? { ...upload, ...change } : upload)));

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useUploads = () => useSyncExternalStore(subscribe, () => uploads);

type Linger = { remaining: number; startedAt: number; timer: ReturnType<typeof setTimeout> | null };

const lingers = new Map<string, Linger>();
let lingerHeld = false;

const armLinger = (id: string, linger: Linger) => {
  linger.startedAt = Date.now();
  linger.timer = setTimeout(() => {
    lingers.delete(id);
    dismiss(id);
  }, linger.remaining);
};

const startLinger = (id: string) => {
  const linger: Linger = { remaining: DONE_LINGER_MS, startedAt: 0, timer: null };
  lingers.set(id, linger);
  if (!lingerHeld) armLinger(id, linger);
};

const clearLinger = (id: string) => {
  const linger = lingers.get(id);
  if (!linger) return;
  if (linger.timer !== null) clearTimeout(linger.timer);
  lingers.delete(id);
};

export const holdDismissal = (held: boolean) => {
  if (held === lingerHeld) return;
  lingerHeld = held;
  for (const [id, linger] of lingers) {
    if (!held) {
      armLinger(id, linger);
    } else if (linger.timer !== null) {
      clearTimeout(linger.timer);
      linger.timer = null;
      linger.remaining = Math.max(0, linger.remaining - (Date.now() - linger.startedAt));
    }
  }
};

class Dismissed extends Error {}

const sha256 = async (file: File, signal: AbortSignal) => {
  if (file.size > SINGLE_REQUEST_BYTES) {
    const hash = new Sha256();
    const reader = file.stream().getReader();
    for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
      if (signal.aborted) {
        void reader.cancel();
        throw new Dismissed();
      }
      hash.update(chunk.value);
    }
    return hash.hex();
  }
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

class UploadRejected extends Error {}
class BytesChanged extends Error {}

const failureOf = (status: number, body: unknown) => {
  if (status === 413) return new UploadRejected(`Larger than ${MAX_UPLOAD_LABEL}.`);
  if (status === 422) return new BytesChanged("The bytes changed in transit.");
  const message = errorOf(status, body).message;
  return status >= 500 ? new Error(message) : new UploadRejected(message);
};

const uploadUrl = (file: File, target: UploadTarget) => {
  if (target.kind === "version")
    return `/api/v1/nodes/${encodeURIComponent(target.nodeId)}/versions`;
  const parent = target.parentId ? `&parent=${encodeURIComponent(target.parentId)}` : "";
  return `/api/v1/uploads?name=${encodeURIComponent(file.name)}${parent}`;
};

const send = (upload: Upload, hash: string) =>
  new Promise<UploadResult>((resolve, reject) => {
    const { file, target } = upload;
    const xhr = new XMLHttpRequest();
    requests.set(upload.id, xhr);
    xhr.open("POST", uploadUrl(file, target));
    xhr.responseType = "json";
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("X-Content-SHA256", hash);
    xhr.setRequestHeader("X-Drive-Client", "1");
    xhr.upload.onprogress = (event) => update(upload.id, { loaded: event.loaded });
    xhr.onload = () => {
      requests.delete(upload.id);
      if (xhr.status === 201) resolve(xhr.response as UploadResult);
      else if (xhr.status === 401) void signIn();
      else reject(failureOf(xhr.status, xhr.response));
    };
    xhr.onerror = () => {
      requests.delete(upload.id);
      reject(new Error("The connection dropped."));
    };
    xhr.onabort = () => requests.delete(upload.id);
    xhr.send(file);
  });

const isLive = (id: string) => uploads.some((item) => item.id === id);

const sendPartOnce = (upload: Upload, started: MultipartUpload, n: number, sent: number) =>
  new Promise<UploadPart>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    requests.set(upload.id, xhr);
    xhr.open("PUT", `/api/v1/uploads/${started.uploadId}/parts/${n}`);
    xhr.responseType = "json";
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-Drive-Client", "1");
    xhr.upload.onprogress = (event) => update(upload.id, { loaded: sent + event.loaded });
    xhr.onload = () => {
      requests.delete(upload.id);
      if (xhr.status === 200) resolve(xhr.response as UploadPart);
      else if (xhr.status === 401) void signIn();
      else reject(failureOf(xhr.status, xhr.response));
    };
    xhr.onerror = () => {
      requests.delete(upload.id);
      reject(new Error("The connection dropped."));
    };
    xhr.onabort = () => {
      requests.delete(upload.id);
      reject(new Dismissed());
    };
    xhr.send(upload.file.slice(sent, sent + started.partSize));
  });

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const sendPart = async (upload: Upload, started: MultipartUpload, n: number) => {
  const sent = (n - 1) * started.partSize;
  for (let attempt = 1; ; attempt++) {
    try {
      return await sendPartOnce(upload, started, n, sent);
    } catch (error) {
      const final = attempt >= PART_ATTEMPTS || !isLive(upload.id);
      if (final || error instanceof Dismissed || error instanceof UploadRejected) throw error;
      await pause(PART_BACKOFF_MS * attempt);
    }
  }
};

const settle = async <D>(request: Promise<{ data?: D; error?: unknown; response: Response }>) => {
  const { data, error, response } = await request;
  if (response.status === 401) return signIn();
  if (!response.ok) throw failureOf(response.status, error);
  return data as D;
};

const startMultipart = (upload: Upload, sha256: string) => {
  const { file, target } = upload;
  const body = {
    contentType: file.type || "application/octet-stream",
    size: file.size,
    sha256,
  };
  return settle(
    target.kind === "version"
      ? client.POST("/nodes/{id}/versions/multipart", {
          params: { path: { id: target.nodeId } },
          body,
        })
      : client.POST("/uploads/multipart", {
          body: { ...body, name: file.name, parentId: target.parentId },
        }),
  );
};

const abortMultipart = (session: MultipartSession | null) => {
  if (session) {
    void client.DELETE("/uploads/{id}", { params: { path: { id: session.started.uploadId } } });
  }
};

const isExpired = (session: MultipartSession) =>
  Date.parse(session.started.expiresAt) <= Date.now();

const openSession = async (upload: Upload, signal: AbortSignal) => {
  const kept = uploads.find((item) => item.id === upload.id)?.multipart ?? null;
  if (kept && !isExpired(kept)) return kept;
  abortMultipart(kept);
  const hash = kept?.hash ?? (await sha256(upload.file, signal));
  if (!isLive(upload.id)) throw new Dismissed();
  update(upload.id, { phase: "uploading" });
  const started = await startMultipart(upload, hash);
  const session: MultipartSession = { hash, started, parts: [] };
  if (!isLive(upload.id)) {
    abortMultipart(session);
    throw new Dismissed();
  }
  update(upload.id, { multipart: session });
  return session;
};

const sendMultipart = async (upload: Upload, signal: AbortSignal): Promise<UploadResult> => {
  const session = await openSession(upload, signal);
  const { started } = session;
  const id = started.uploadId;
  let { parts } = session;
  update(upload.id, { phase: "uploading", loaded: parts.length * started.partSize });
  try {
    for (let n = parts.length + 1; n <= started.partCount; n++) {
      if (!isLive(upload.id)) throw new Dismissed();
      parts = [...parts, await sendPart(upload, started, n)];
      update(upload.id, { multipart: { ...session, parts } });
    }
    return await settle(
      client.POST("/uploads/{id}/complete", {
        params: { path: { id } },
        body: { parts },
        headers: { "Idempotency-Key": id.slice(-43) },
      }),
    );
  } catch (error) {
    if (error instanceof UploadRejected || error instanceof BytesChanged) {
      abortMultipart(session);
      update(upload.id, { multipart: null });
    }
    throw error;
  }
};

const sendSingle = async (upload: Upload, signal: AbortSignal) => {
  const hash = await sha256(upload.file, signal);
  if (!isLive(upload.id)) throw new Dismissed();
  update(upload.id, { phase: "uploading" });
  return send(upload, hash);
};

const analyse = async (upload: Upload, result: UploadResult) => {
  if (!analysisLabel(upload.file)) {
    startLinger(upload.id);
    return;
  }
  update(upload.id, { analysing: true });
  await queueAnalysis(upload.file, {
    nodeId: result.node.id,
    version: result.version.version,
  });
  if (!isLive(upload.id)) return;
  update(upload.id, { analysing: false });
  startLinger(upload.id);
};

const run = async (upload: Upload) => {
  const { file, target } = upload;
  if (file.size > MAX_UPLOAD_BYTES) {
    update(upload.id, {
      phase: "failed",
      error: `Larger than ${MAX_UPLOAD_LABEL}.`,
      retryable: false,
    });
    return;
  }
  if (file.size === 0) {
    update(upload.id, { phase: "failed", error: "The file is empty.", retryable: false });
    return;
  }

  update(upload.id, { phase: "preparing", loaded: 0, error: null });
  const hashAbort = new AbortController();
  hashing.set(upload.id, hashAbort);
  try {
    const result =
      file.size > SINGLE_REQUEST_BYTES
        ? await sendMultipart(upload, hashAbort.signal)
        : await sendSingle(upload, hashAbort.signal);
    update(upload.id, { phase: "done", loaded: file.size, result, multipart: null });
    markLanded(result.node.id, versionKey(result.node.id, result.version.version));
    flashMood("happy", 1400);
    storeNode(queryClient, result.node);
    refreshAfterUpload(queryClient, target.parentId);
    if (target.kind === "version") {
      void queryClient.invalidateQueries({ queryKey: keys.node(target.nodeId) });
    }
    void analyse(upload, result);
  } catch (error) {
    if (!isLive(upload.id)) return;
    update(upload.id, {
      phase: "failed",
      error: error instanceof Error ? error.message : "The upload failed.",
      retryable: !(error instanceof UploadRejected),
    });
  } finally {
    hashing.delete(upload.id);
  }
};

const pump = () => {
  const active = uploads.filter((u) => u.phase === "preparing" || u.phase === "uploading").length;
  const next = uploads.filter((u) => u.phase === "queued").slice(0, CONCURRENCY - active);
  for (const upload of next) {
    update(upload.id, { phase: "preparing" });
    void run(upload).finally(pump);
  }
};

export const enqueue = (files: Iterable<File>, target: UploadTarget = ROOT_TARGET) => {
  const added = [...files].map(
    (file): Upload => ({
      id: crypto.randomUUID(),
      file,
      target,
      phase: "queued",
      loaded: 0,
      error: null,
      retryable: true,
      result: null,
      analysing: false,
      multipart: null,
    }),
  );
  if (added.length === 0) return;
  emit([...uploads, ...added]);
  pump();
};

export const retry = (id: string) => {
  clearLinger(id);
  update(id, { phase: "queued", loaded: 0, error: null, analysing: false });
  pump();
};

export const dismiss = (id: string) => {
  clearLinger(id);
  hashing.get(id)?.abort();
  requests.get(id)?.abort();
  abortMultipart(uploads.find((upload) => upload.id === id)?.multipart ?? null);
  emit(uploads.filter((upload) => upload.id !== id));
  pump();
};

export const dismissSettled = () => {
  for (const upload of uploads) {
    if (upload.phase !== "done" && upload.phase !== "failed") continue;
    clearLinger(upload.id);
    abortMultipart(upload.multipart);
  }
  emit(uploads.filter((upload) => upload.phase !== "done" && upload.phase !== "failed"));
};
